/*
 * Copyright (c) Funtip GbR 2018.
 * Simon Oldeboershuis, Perry Prust
 */

import {push} from '@lagunovsky/redux-react-router';
import {Token} from 'client-oauth2';
import {all, delay, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {Action} from 'typescript-fsa';
import {postAccessTokenToLoginIframe} from '../../App';
import {FuntipPushSubscription2} from '../../services/webapp';
import {fetchWrapperFunc, fetchWrapperFuncNoLoading} from '../../utils';
import {
    accountUserSettingClearProfileSettings,
    accountUserSettingLoadUserSettings,
} from '../AccountUserApp/AccountUserSettings/actions';
import {notificationStatusClear} from '../BettingApp/BettingProfile/NotificationStatus/actions';
import {
    tournamentSelectionLoadAvailableTournaments,
    tournamentSelectionLoadDefaultTournament,
} from '../TournamentSelectionComponent/actions';
import {loadStaticHtmlFragments} from './HtmlInclude/actions';
import {
    addSystemErrorMessage,
    HANDLE_FETCH_EXCEPTION,
    LOAD_MY_USER_INFO,
    LOAD_TOKEN_FROM_STORAGE,
    LOAD_USER_ROLES,
    loadMyUserInfo,
    loadTokenFromStorage,
    loadUserRoles,
    LOGIN_ACTION,
    loginAction,
    logout,
    LOGOUT_ACTION,
    SET_AUTHENTICATED_USER,
    setSystemAccessToken,
    setSystemUserInfo,
    setUserRoles,
    SYSTEM_AUTOMATIC_LOGOUT,
    SYSTEM_CHECK_SERVER_AVAILABILITY,
    SYSTEM_CONFIRM_AGB,
    SYSTEM_LOAD_AGB,
    SYSTEM_REFUSE_AGB,
    SYSTEM_SET_AUTHENTICATED_ACCESS_TOKEN,
    SYSTEM_STARTUP,
    systemLoadAgbs,
    systemLoggedOut,
    systemSetAgbs,
    systemSetAuthenticatedAccessToken,
    systemSetDeploymentProperties,
    systemSetServerAvailibility,
    systemShowAutomaticLogoutMessage,
} from './actions';
import {systemAccessTokenSelector} from './selectors';
import {getFetchHeaders} from './system';
import {
    ACCESS_TOKEN_KEY,
    getAliveApi,
    getAuthToken,
    getProfile2Api,
    getSystemConfig,
    isWebKit,
    registerAuthToken,
    ServerType,
    unregisterAuthToken,
} from './systemConfig';

const profileApi = getProfile2Api();

function* loadAgbInfoGenerator() {
    yield* fetchWrapperFuncNoLoading(
        () => getProfile2Api().getAgbInfos({tenant: getSystemConfig().clientName}),
        [(r) => systemSetAgbs(r)],
    );
}

function* acceptPrivacyStatementGenerator() {
    yield* fetchWrapperFunc(() => getProfile2Api().acceptPrivacyStatement());
    yield put(systemLoadAgbs());
}

function* refusePrivacyStatementGenerator() {
    yield put(logout());
}

function* loadTokenFromStorageGenerator() {
    let token: string = yield select(systemAccessTokenSelector);

    const headers = (window as any).__HTTP_HEADERS___;
    if (headers) {
        const authHeader = headers['authorization'];
        if (authHeader) {
            const auth: string = authHeader[0] ? authHeader[0].value : '';
            token = auth.replace('Bearer ', '');
            if (token) {
                yield put(systemSetAuthenticatedAccessToken(token));
            }
        }
    }

    if (!token) {
        const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (storedToken) {
            yield put(systemSetAuthenticatedAccessToken(storedToken));
            registerAuthToken(storedToken);
            token = storedToken;
        }
    }

    if (!token) {
        try {
            if (isWebKit()) {
                getAuthToken();
            }
        } catch (err) {
            console.info(err);
        }
    }
}

function* setAuthenticatedUser(action: Action<Token>) {
    const accessToken = action.payload.accessToken;
    if (accessToken) {
        yield put(systemSetAuthenticatedAccessToken(accessToken));
        registerAuthToken(accessToken);
    }
}

function* setAuthenticatedAccessTokenGenerator(action: Action<string>) {
    const accessToken = action.payload;
    yield put(setSystemAccessToken(accessToken));

    if (accessToken !== undefined && accessToken !== 'undefined') {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
    }

    yield put(loginAction());
    yield put(loadMyUserInfo());
    yield put(loadUserRoles());
}

function* logoutGenerator() {
    const token: string = yield select(systemAccessTokenSelector);

    if (token) {
        unregisterAuthToken(token);

        const header: RequestInit = {...getFetchHeaders(), credentials: 'include'};
        yield* fetchWrapperFunc(() => profileApi.logout({tenant: getSystemConfig().clientName}, header));
    }

    // remove token from local storage
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    console.info('remove actionToken from iframe');
    postAccessTokenToLoginIframe(undefined);
    yield delay(500);

    yield put(systemLoggedOut());
    yield put(setSystemAccessToken(''));
    yield put(setUserRoles([]));
    yield put(notificationStatusClear());
    yield put(accountUserSettingClearProfileSettings());

    yield put(push('/'));
}

function* automaticLogout() {
    yield put(logout());
    yield put(systemShowAutomaticLogoutMessage(true));
}

function* loginGenerator() {
    yield put(systemLoadAgbs());
    yield put(accountUserSettingLoadUserSettings());
}

function* handleFetchExceptionGenerator(action: Action<Response>) {
    const err = action.payload;
    if (err.status === 401 || err.status === 403) {
        // unauthorized or forbidden
        // yield put(systemAutomaticLogout());
    } else {
        yield put(addSystemErrorMessage(err));
    }
}

function* loadUserInfoGenerator() {
    yield* fetchWrapperFuncNoLoading(
        () => profileApi.getMyUserProfile({tenant: getSystemConfig().clientName}),
        [(r) => setSystemUserInfo(r)],
    );
}

function* loadUserRoleGenerator() {
    yield* fetchWrapperFuncNoLoading(
        () => profileApi.getRoles({tenant: getSystemConfig().clientName}),
        [(r) => setUserRoles(r)],
    );
}

const loadDeploymentProperties = () => {
    return fetch('https://tippspiel.funtip.de/radioeins.json').then((r) => r.json());
};

function* systemStartupGenerator() {
    yield put(loadTokenFromStorage());

    yield put(systemLoadAgbs());

    // load the external deployment properties from tippspiel.funtip.de
    yield* fetchWrapperFunc(() => loadDeploymentProperties(), [(r) => systemSetDeploymentProperties(r)]);

    yield put(loadStaticHtmlFragments());
    // yield put(fetchActiveTournaments());
    // yield put(fetchDefaultTournament());
    yield put(tournamentSelectionLoadDefaultTournament());
    yield put(tournamentSelectionLoadAvailableTournaments());
}

function* checkServerAvailability() {
    const servers: ServerType[] = ['betting', 'account', 'image', 'playlist'];

    for (let server of servers) {
        yield* fetchWrapperFuncNoLoading(
            () => getAliveApi(server as ServerType).ping1(),
            [(r) => systemSetServerAvailibility({server: server as ServerType, available: true})],
            () => systemSetServerAvailibility({server: server as ServerType, available: false}),
        );
    }
}

export const pushSubscription2FuntipPushSubscription = (sub: PushSubscription): FuntipPushSubscription2 | undefined => {
    const p256dh: ArrayBuffer | null = sub.getKey('p256dh');
    const auth: ArrayBuffer | null = sub.getKey('auth');

    if (p256dh !== null && auth !== null) {
        const funtipPushSubscription: FuntipPushSubscription2 = {
            endpoint: sub.endpoint,
            keys: {
                p256dh: sub.toJSON()?.keys?.p256dh || '',
                auth: sub.toJSON()?.keys?.auth || '',
            },
        };
        return funtipPushSubscription;
    }
    return undefined;
};

function* watchForSystemActions() {
    yield takeEvery(LOAD_TOKEN_FROM_STORAGE, loadTokenFromStorageGenerator);
    yield takeEvery(SET_AUTHENTICATED_USER, setAuthenticatedUser);
    yield takeEvery(SYSTEM_SET_AUTHENTICATED_ACCESS_TOKEN, setAuthenticatedAccessTokenGenerator);
    yield takeEvery(LOAD_MY_USER_INFO, loadUserInfoGenerator);
    yield takeEvery(LOAD_USER_ROLES, loadUserRoleGenerator);
    yield takeEvery(HANDLE_FETCH_EXCEPTION, handleFetchExceptionGenerator);
    yield takeEvery(LOGOUT_ACTION, logoutGenerator);
    yield takeLatest(SYSTEM_STARTUP, systemStartupGenerator);
    yield takeLatest(LOGIN_ACTION, loginGenerator);
    yield takeLatest(SYSTEM_LOAD_AGB, loadAgbInfoGenerator);
    yield takeLatest(SYSTEM_CONFIRM_AGB, acceptPrivacyStatementGenerator);
    yield takeLatest(SYSTEM_REFUSE_AGB, refusePrivacyStatementGenerator);
    yield takeLatest(SYSTEM_CHECK_SERVER_AVAILABILITY, checkServerAvailability);
    yield takeLatest(SYSTEM_AUTOMATIC_LOGOUT, automaticLogout);
}

export function* systemSagas() {
    yield all([watchForSystemActions()]);
}
